import { useEffect, useMemo, useRef, useState } from "react";
import { fetchCoachMyTeams } from "../../../auth/Onboarding/services/onboardingServices";
import { teamKeyOf } from "../allocationModel";

const genderOfLeague = (t) => {
  const lg = (t?.league || "").toLowerCase();
  if (lg.endsWith("wb")) return "F";
  if (lg.endsWith("mb") || lg.endsWith("fb")) return "M";
  return "";
};

const byKey = (list, key) => (key ? list.find((t) => teamKeyOf(t) === key) : undefined);

// The view that holds the team picked on another Coach Mode tab, else the
// coach's own teams when they have any.
const initialView = (teams, preferred) => {
  const picked = preferred && byKey(teams, teamKeyOf(preferred));
  if (picked) return picked.leagueAdmin ? "league" : "coach";
  return teams.some((t) => !t.leagueAdmin) ? "coach" : "league";
};

// The verified coach's teams for the CacheShare™ team switch: their own
// teams (primary first) and, for league admins, every team in the leagues
// they run (by gender). `fallback` (the primary team from the coach
// profile) stands in when the team list cannot be loaded. `preferred` is the
// team picked on another Coach Mode tab: it opens unless the coach picks
// another one here.
export const useCoachTeams = (fallback, preferred = null) => {
  const [teams, setTeams] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeKey, setActiveKey] = useState("");
  const [viewMode, setViewMode] = useState("coach");
  const [gender, setGender] = useState("all");
  const preferredRef = useRef(preferred);
  preferredRef.current = preferred;

  useEffect(() => {
    let cancelled = false;
    fetchCoachMyTeams()
      .then((list) => {
        if (cancelled) return;
        const all = list?.length ? list : fallback ? [fallback] : [];
        setTeams(all);
        setViewMode(initialView(all, preferredRef.current));
      })
      .catch(() => {
        if (!cancelled) setTeams(fallback ? [fallback] : []);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const own = useMemo(() => teams.filter((t) => !t.leagueAdmin), [teams]);
  const league = useMemo(() => teams.filter((t) => t.leagueAdmin), [teams]);
  const choices = useMemo(() => {
    if (league.length && viewMode === "league") return gender === "all" ? league : league.filter((t) => genderOfLeague(t) === gender);
    return own;
  }, [own, league, viewMode, gender]);
  const active =
    byKey(choices, activeKey) || byKey(choices, preferred ? teamKeyOf(preferred) : "") || choices.find((t) => t.isPrimary) || choices[0] || null;

  // A new view starts from the shared pick again.
  const onViewMode = (mode) => {
    setViewMode(mode);
    setActiveKey("");
  };

  return {
    loaded,
    teams: choices,
    active,
    activeKey: active ? teamKeyOf(active) : "",
    setActiveKey,
    leagueAdmin: league.length ? { viewMode, onViewMode, gender, onGender: setGender, showViewSwitch: own.length > 0 } : null,
  };
};
